import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { captureException, track } from "@/lib/analytics";
import { AlertCircle, ArrowRight, Check, Loader2, Mail, MessageCircle, ShieldCheck } from "lucide-react";

const CONTACT_API_URL =
  import.meta.env.VITE_CONTACT_API_URL ?? "https://app.coach-os.eu/api/contact/public";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("Demande de demo CoachOS");
  const [message, setMessage] = useState("");
  const [intent, setIntent] = useState<"demo" | "message">("demo");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    name.trim().length >= 2 &&
    email.trim().includes("@") &&
    subject.trim().length >= 3 &&
    message.trim().length >= 10;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSent(false);

    if (!canSubmit) {
      setError("Completez votre nom, email, sujet et message avant l'envoi.");
      track("lead_form_validation_failed", {
        form: "contact",
        intent,
        has_name: name.trim().length >= 2,
        has_email: email.trim().includes("@"),
        has_subject: subject.trim().length >= 3,
        has_message: message.trim().length >= 10,
      });
      return;
    }

    setSubmitting(true);
    track("lead_form_submitted", {
      form: "contact",
      intent,
      subject,
    });
    try {
      const res = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          intent,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error ?? "Impossible d'envoyer le message pour le moment.");
        track("lead_form_failed", {
          form: "contact",
          intent,
          status: res.status,
          error: data.error ?? "unknown",
        });
        return;
      }

      setSent(true);
      track("lead_form_succeeded", {
        form: "contact",
        intent,
      });
      setName("");
      setEmail("");
      setSubject("Demande de demo CoachOS");
      setMessage("");
      setIntent("demo");
    } catch (err) {
      setError("Connexion impossible. Verifiez votre reseau puis reessayez.");
      captureException(err, {
        form: "contact",
        intent,
      });
      track("lead_form_failed", {
        form: "contact",
        intent,
        error: "network",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-[#dfe2ec] bg-white px-4 py-3 text-sm text-[#0e1220] placeholder:text-[#0e1220]/45 outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20";
  const labelCls = "font-mono text-[11px] font-semibold uppercase tracking-widest text-[#0e1220]/50";

  return (
    <section id="contact" className="relative overflow-hidden py-16 md:py-28">
      <div className="container relative z-10">
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">

          {/* ── Gauche : titre + réassurance ── */}
          <motion.div
            initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
           className="contact-editorial">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#2563EB]">À vous de jouer</p>
            <h2 className="landing-section-title max-w-xl text-3xl font-extrabold leading-[1.05] tracking-tight text-[#0e1220] md:text-5xl">
              Votre prochain chapitre.<br /><span className="text-[#2563EB]">On le prépare ensemble ?</span>
            </h2>
            <p className="mt-5 max-w-lg leading-relaxed text-[#0e1220]/60">
              Dites-nous comment vous suivez vos clients aujourd'hui, ce qui vous ralentit,
              et ce que vous voulez rendre plus simple dans votre organisation.
            </p>

            <div className="mt-9 border-t border-[#e7e9f1]">
              {[
                "Réponse humaine, pas de séquence automatique",
                "Démo adaptée à votre volume de clients",
                "Questions produit, données et onboarding au même endroit",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 border-b border-[#e7e9f1] py-4">
                  <Check className="h-4 w-4 shrink-0 text-[#2563EB]" />
                  <span className="text-sm text-[#0e1220]/70">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Droite : formulaire ── */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-[#e7e9f1] bg-white p-6 md:p-8 shadow-[0_1px_3px_rgba(16,20,40,0.05)]"
          >
            <div className="mb-7 flex items-start justify-between gap-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#2563EB]/30 bg-[#2563EB]/10 text-[#2563EB]">
                  {intent === "demo" ? <MessageCircle className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                </div>
                <div>
                  <p className="text-base font-bold text-[#0e1220]">
                    {intent === "demo" ? "Demander une démo" : "Message direct"}
                  </p>
                  <p className="mt-1 text-sm text-[#0e1220]/55">Réponse sous 24 à 48h ouvrées.</p>
                </div>
              </div>
              <span className="hidden rounded-full border border-[#e7e9f1] bg-[#eef0f6] px-3 py-1 font-mono text-xs text-[#0e1220]/55 sm:inline-flex">
                CoachOS
              </span>
            </div>

            <div className="mb-5 grid gap-1 rounded-full border border-[#e7e9f1] bg-[#eef0f6] p-1 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  track("lead_form_intent_changed", {
                    form: "contact",
                    intent: "demo",
                  });
                  setIntent("demo");
                  setSubject("Demande de demo CoachOS");
                }}
                className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${
                  intent === "demo"
                    ? "bg-[#2563EB] text-white"
                    : "text-[#0e1220]/55 hover:text-[#0e1220]"
                }`}
              >
                Demande de démo
              </button>
              <button
                type="button"
                onClick={() => {
                  track("lead_form_intent_changed", {
                    form: "contact",
                    intent: "message",
                  });
                  setIntent("message");
                  setSubject("Demande de contact CoachOS");
                }}
                className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${
                  intent === "message"
                    ? "bg-[#2563EB] text-white"
                    : "text-[#0e1220]/55 hover:text-[#0e1220]"
                }`}
              >
                Message
              </button>
            </div>

            <div className="mb-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2">
                  <span className={labelCls}>Nom</span>
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className={inputCls}
                    placeholder="Votre nom"
                  />
                </label>
                <label className="space-y-2">
                  <span className={labelCls}>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className={inputCls}
                    placeholder="vous@email.fr"
                  />
                </label>
              </div>

              <label className="block space-y-2">
                <span className={labelCls}>Sujet</span>
                <input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  className={inputCls}
                  placeholder="Sujet de votre message"
                />
              </label>

              <label className="block space-y-2">
                <span className={labelCls}>Message</span>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={5}
                  className={`${inputCls} resize-none leading-relaxed`}
                  placeholder="Votre activite, nombre de clients, ce que vous voulez simplifier..."
                />
              </label>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <p className="text-xs leading-relaxed text-red-600">{error}</p>
              </div>
            )}

            {sent && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#2563EB]/30 bg-[#eef1ff] px-4 py-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
                <p className="text-xs leading-relaxed text-[#0e1220]/80">
                  Message envoye. On revient vers vous rapidement.
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="mb-5 h-12 w-full gap-2 rounded-full bg-[#2563EB] text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition hover:bg-[#1d4ed8]"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Envoi..." : "Envoyer la demande"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </Button>

            <div className="flex items-start gap-3 border-t border-[#e7e9f1] pt-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
              <p className="text-xs leading-relaxed text-[#0e1220]/55">
                Réponse sous 24 à 48h ouvrées. Vos questions produit,
                onboarding ou donnees restent traitees directement avec l'equipe CoachOS.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
